
import { useEffect, useRef, useState } from 'react';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input as _Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { IconAlertCircle } from '@tabler/icons-react';
import { SUPPORTED_LANGUAGES, NATIVE_LANGUAGE_NAMES } from '@/lib/language-service';
import { ContactInfoFormSection } from '@/components/forms';
import { ProfilePictureUpload } from './ProfilePictureUpload';
import { getAttachmentsForEntity } from '@/features/attachments/operations';
import { componentLogger } from '@/lib/logging';
import type { UseFormReturn } from 'react-hook-form';
import type { ProfileFieldsSectionProps } from './UserDialogTypes';

export function ProfileFieldsSection({
  form,
  isSubmittingForm,
  isEdit: _isEdit,
  mode,
  t,
  duplicateEmailError = false,
  onClearDuplicateEmailError,
  userToEdit
}: ProfileFieldsSectionProps) {
  // Watch email field and clear error when it changes
  const emailValue = form.watch('email');
  const prevEmailRef = useRef(emailValue);

  // State for existing profile picture attachment ID
  const [existingPictureId, setExistingPictureId] = useState<string | undefined>(undefined);
  const [fetchingPictureId, setFetchingPictureId] = useState(false);

  useEffect(() => {
    // Only clear error when email value actually changes (user is typing)
    if (emailValue !== prevEmailRef.current && duplicateEmailError && onClearDuplicateEmailError) {
      onClearDuplicateEmailError();
    }
    prevEmailRef.current = emailValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue, onClearDuplicateEmailError]);

  // Fetch existing profile picture attachment ID when editing a user
  useEffect(() => {
    const fetchExistingProfilePicture = async () => {
      if (!userToEdit?.id) {
        setExistingPictureId(undefined);
        return;
      }

      setFetchingPictureId(true);
      try {
        // Use generic attachment system to fetch profile picture
        const attachments = await getAttachmentsForEntity({
          parentEntityId: userToEdit.id,
          parentEntityType: 'PROFILE'
        });

        if (attachments && attachments.length > 0) {
          // Take the first attachment (most recent)
          const firstAttachment = attachments[0];
          if (firstAttachment) {
            componentLogger.debug('ProfileFieldsSection', 'Found existing profile picture', {
              attachmentId: firstAttachment.id,
              userId: userToEdit.id
            });
            setExistingPictureId(firstAttachment.id);
          } else {
            componentLogger.debug('ProfileFieldsSection', 'No profile picture found for user', { userId: userToEdit.id });
            setExistingPictureId(undefined);
          }
        } else {
          componentLogger.debug('ProfileFieldsSection', 'No profile picture found for user', { userId: userToEdit.id });
          setExistingPictureId(undefined);
        }
      } catch (err) {
        componentLogger.error('ProfileFieldsSection', 'Error fetching profile picture', {
          error: err,
          userId: userToEdit.id
        });
        setExistingPictureId(undefined);
      } finally {
        setFetchingPictureId(false);
      }
    };

    fetchExistingProfilePicture();
  }, [userToEdit?.id]);
  return (
    <>
      {/* Duplicate Email Error Alert */}
      {duplicateEmailError && mode === 'create' && (
        <Alert variant="destructive" className="mb-4">
          <IconAlertCircle className="h-4 w-4" />
          <AlertTitle>{t('usersActionDialog.errors.duplicateEmailTitle')}</AlertTitle>
          <AlertDescription>
            {t('usersActionDialog.errors.duplicateEmailDescription')}
          </AlertDescription>
        </Alert>
      )}

      {/* Contact Information using shared component */}
      <ContactInfoFormSection
        form={form as unknown as UseFormReturn<Record<string, unknown>>}
        fields={{
          email: mode === 'create',
          firstName: true,
          lastName: true,
          phone: true,
        }}
        required={{
          email: mode === 'create',
          firstName: true,
          lastName: true,
          phone: false,
        }}
        disabled={isSubmittingForm}
        fieldPrefix=""
        fieldNames={{
          phone: 'phoneNumber',
        }}
      />

      {/* Title Field */}
      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('profile.title_label')}</FormLabel>
            <FormControl>
              <_Input
                placeholder={t('profile.title_placeholder')}
                disabled={isSubmittingForm}
                {...field}
                value={field.value || ''}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Profile Picture Upload */}
      <ProfilePictureUpload
        userId={userToEdit?.id}
        existingPictureId={existingPictureId}
        currentImage={(() => {
          const pictureData = form.watch('profilePictureData');
          return pictureData ? {
            tempStoragePath: pictureData.tempStoragePath,
            fileName: pictureData.fileName,
          } : undefined;
        })()}
        onImageUploaded={(imageData) => {
          form.setValue('profilePictureData', imageData);
        }}
        onImageRemoved={() => {
          form.setValue('profilePictureData', undefined);
          // Clear the existing picture ID when removing
          setExistingPictureId(undefined);
        }}
        disabled={isSubmittingForm || fetchingPictureId}
        userInitials={`${form.watch('firstName')?.[0] || ''}${form.watch('lastName')?.[0] || ''}`.toUpperCase() || 'U'}
      />

      {/* Language Selection Field */}
      <FormField
        control={form.control}
        name="language"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('usersActionDialog.labels.language')}</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value || 'en'}
              disabled={isSubmittingForm}
            >
              <FormControl>
                <SelectTrigger data-testid="language-select">
                  <SelectValue placeholder={t('usersActionDialog.placeholders.selectLanguage')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <SelectItem key={lang} value={lang} data-testid={`language-option-${lang}`}>
                    {NATIVE_LANGUAGE_NAMES[lang]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}